import { Button } from '@startuptools/ui/joy/Button';
import { Controller } from 'react-hook-form';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Trans, useLingui } from '@lingui/react';
import { Switch } from '@startuptools/ui/joy/Switch';
import { Box } from '@startuptools/ui/joy/Box';
import { Stack } from '@startuptools/ui/joy/Stack';
import { useForm } from 'react-hook-form';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import { FormLabel } from '@startuptools/ui/joy/FormLabel';
import { FormHelperText } from '@startuptools/ui/joy/FormHelperText';
import BigNumber from 'bignumber.js';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { useFieldSync } from '@startuptools/common/react-hooks';

export type FormData = {
  changeShareLimits: boolean;
  minimumNumberOfShares: string;
  maximumNumberOfShares: string;
  minimumShareCapital: string;
  maximumShareCapital: string;
};

export const StepShareLimits = ({
  quotaValueAfter,
  changeShareLimits,
  minimumNumberOfShares,
  maximumNumberOfShares,
  minimumShareCapital,
  maximumShareCapital,
  numberOfSharesAfter,
  onSave,
  stepNext,
  onToggle,
  mustBeInRange,
  loading,
}: {
  quotaValueAfter: string;
  numberOfSharesAfter: string;
  onSave: (data: FormData) => Promise<void>;
  stepNext: () => void;
  onToggle?: (toggle: boolean) => Promise<void>;
  mustBeInRange?: BigNumber | null | undefined; // a number that the minimum and maximum number of shares must be in range of
  loading?: boolean;
} & FormData) => {
  const { i18n } = useLingui();
  const form = useForm<FormData>({
    defaultValues: {
      changeShareLimits,
      minimumNumberOfShares,
      maximumNumberOfShares,
      minimumShareCapital,
      maximumShareCapital,
    },
  });

  useFieldSync({
    form,
    fields: {
      maximumNumberOfShares: v => {
        if (!v) return {};
        return { maximumShareCapital: new BigNumber(quotaValueAfter).times(v).toFixed(6) };
      },
      minimumNumberOfShares: v => {
        if (!v) return {};
        return { minimumShareCapital: new BigNumber(quotaValueAfter).times(v).toFixed(6) };
      },
    },
  });

  return (
    <Stack gap={2}>
      <Box sx={{ mt: 2 }}>
        <Controller
          control={form.control}
          name="changeShareLimits"
          key="changeShareLimits"
          render={({ field: { value, onChange } }) => (
            <Switch
              checked={value}
              onChange={async () => {
                await onToggle?.(!value);
                onChange(!value);
              }}
              color={value ? 'primary' : 'neutral'}
              variant={value ? 'solid' : 'outlined'}
              endDecorator={<Trans id="Change share limits" />}
              slotProps={{
                endDecorator: {
                  sx: {
                    minWidth: 24,
                  },
                },
              }}
            />
          )}
        />
      </Box>

      {form.watch('changeShareLimits') ? (
        <form onSubmit={form.handleSubmit(onSave)}>
          <Stack gap={2} maxWidth={800}>
            <Controller
              control={form.control}
              name="minimumNumberOfShares"
              key="minimumNumberOfShares"
              rules={{
                required: true,
                min: 0,
                validate: value => {
                  if (mustBeInRange && value && new BigNumber(value).gt(new BigNumber(mustBeInRange))) {
                    return 'not_in_range';
                  }
                  return true;
                },
              }}
              render={({ field: { value, onChange } }) => {
                return (
                  <FormControl error={!!form.formState.errors.minimumNumberOfShares}>
                    <FormLabel>
                      <Trans id="Minimum number of shares" />
                    </FormLabel>
                    <NumericFormatInput
                      sx={{ maxWidth: '200px' }}
                      value={value ?? ''}
                      onChange={onChange}
                      lang={i18n.locale}
                      decimalScale={0}
                    />
                    {new BigNumber(value ?? '0').gt(numberOfSharesAfter) && (
                      <FormHelperText color="warning">
                        <Trans id="Note: this is greater than the current total number of shares. This is only correct if the number of shares changes in the same registration." />
                      </FormHelperText>
                    )}
                    {form.formState.errors.minimumNumberOfShares?.message === 'not_in_range' && (
                      <FormHelperText>
                        <Trans
                          id="Can't be more than total number of shares ({totalShares})"
                          values={{
                            totalShares: (
                              <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                                {mustBeInRange}
                              </NumberFormat>
                            ),
                          }}
                        />
                      </FormHelperText>
                    )}
                  </FormControl>
                );
              }}
            />
            <Controller
              control={form.control}
              name="maximumNumberOfShares"
              key="maximumNumberOfShares"
              rules={{
                required: true,
                validate: value => {
                  const min = form.getValues('minimumNumberOfShares');
                  if (value && new BigNumber(value).lt(min)) {
                    return 'min_greater_than_max';
                  }
                  if (value && new BigNumber(value).gt(new BigNumber(min).times(4))) {
                    return 'max_too_great';
                  }
                  if (mustBeInRange && value && new BigNumber(value).lt(new BigNumber(mustBeInRange))) {
                    return 'not_in_range';
                  }
                  return true;
                },
              }}
              render={({ field: { value, onChange } }) => (
                <FormControl error={!!form.formState.errors.maximumNumberOfShares}>
                  <FormLabel>
                    <Trans id="Maximum number of shares" />
                  </FormLabel>
                  <NumericFormatInput
                    sx={{ maxWidth: '200px' }}
                    value={value ?? ''}
                    onChange={onChange}
                    lang={i18n.locale}
                    decimalScale={0}
                  />
                  {new BigNumber(value ?? '0').lt(new BigNumber(numberOfSharesAfter)) && (
                    <FormHelperText color="warning">
                      <Trans id="Note: this is less than the current total number of shares. This is only correct if the number of shares is reduced in the same registration." />
                    </FormHelperText>
                  )}
                  {form.formState.errors.maximumNumberOfShares && (
                    <FormHelperText>
                      {form.formState.errors.maximumNumberOfShares.message === 'min_greater_than_max' && (
                        <Trans id="Can't be less than minimum number of shares" />
                      )}
                      {form.formState.errors.maximumNumberOfShares.message === 'max_too_great' && (
                        <Trans id="Can't be more than 4 times the minimum number of shares" />
                      )}
                      {form.formState.errors.maximumNumberOfShares.message === 'not_in_range' && (
                        <Trans
                          id="Can't be less than total number of shares ({totalShares})"
                          values={{
                            totalShares: (
                              <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                                {mustBeInRange}
                              </NumberFormat>
                            ),
                          }}
                        />
                      )}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <Stack gap={1}>
              <FormHelperText>
                <Trans
                  id="Lowest share capital: {number}"
                  values={{
                    number: (
                      <NumberFormat locale={i18n.locale} minimumFractionDigits={0} maximumFractionDigits={6}>
                        {form.watch('minimumShareCapital')}
                      </NumberFormat>
                    ),
                  }}
                />
              </FormHelperText>
              <FormHelperText>
                <Trans
                  id="Highest share capital: {number}"
                  values={{
                    number: (
                      <NumberFormat locale={i18n.locale} minimumFractionDigits={0} maximumFractionDigits={6}>
                        {form.watch('maximumShareCapital')}
                      </NumberFormat>
                    ),
                  }}
                />
              </FormHelperText>
            </Stack>

            <Stack direction="row" justifyContent="flex-start">
              <Button type="submit" loading={loading}>
                <Trans id="Next" />
              </Button>
            </Stack>
          </Stack>
        </form>
      ) : (
        <Box>
          <Button onClick={() => stepNext()}>
            <Trans id="Skip" />
          </Button>
        </Box>
      )}
    </Stack>
  );
};
